import { useEffect, useRef, useState } from "react";
import { useYouTubeAPI } from "@/hooks/useYouTubeAPI";
import { Loader2 } from "lucide-react";

interface YouTubePlayerProps {
  videoId: string;
  onTimeRemaining?: (seconds: number) => void;
  showButtonAt?: number; // seconds remaining
  onVideoEnd?: () => void; // called when video ends
}

export const YouTubePlayer = ({ 
  videoId, 
  onTimeRemaining,
  showButtonAt = 10,
  onVideoEnd
}: YouTubePlayerProps) => {
  const isAPIReady = useYouTubeAPI();
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const playerId = `youtube-player-${videoId}`;

  useEffect(() => {
    if (!isAPIReady || !containerRef.current) return;

    // Clean up existing player
    if (playerRef.current) {
      try {
        playerRef.current.destroy();
      } catch (e) {
        console.error("Error destroying player:", e);
      }
      playerRef.current = null;
    }

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      try {
        playerRef.current = new window.YT.Player(playerId, {
          videoId: videoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            rel: 0,
            modestbranding: 1
          },
          events: {
            onReady: () => setIsPlayerReady(true),
            onStateChange: onPlayerStateChange
          }
        });
      } catch (e) {
        console.error("Error creating player:", e);
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {
          console.error("Error destroying player on cleanup:", e);
        }
        playerRef.current = null;
      }
    };
  }, [isAPIReady, videoId]);

  const onPlayerStateChange = (event: any) => {
    if (event.data === window.YT.PlayerState.PLAYING) {
      checkTimeRemaining();
    }
    if (event.data === window.YT.PlayerState.ENDED && onVideoEnd) {
      onVideoEnd();
    }
  };

  const checkTimeRemaining = () => {
    const interval = setInterval(() => {
      if (playerRef.current && playerRef.current.getDuration) {
        try {
          const duration = playerRef.current.getDuration();
          const currentTime = playerRef.current.getCurrentTime();
          const timeRemaining = duration - currentTime;
          
          if (onTimeRemaining) {
            onTimeRemaining(timeRemaining);
          }

          if (timeRemaining <= showButtonAt && timeRemaining > 0) {
            clearInterval(interval);
          }
        } catch (e) {
          clearInterval(interval);
        }
      }
    }, 1000);
  };

  return (
    <div className="relative">
      {!isPlayerReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 rounded-lg z-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      <div ref={containerRef} className="relative" style={{ padding: "56.25% 0 0 0" }}>
        <div 
          id={playerId}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: "0.5rem"
          }}
        />
      </div>
    </div>
  );
};
